import { WEAPON_Y_OFFSET } from "../models/player";
import { drawAttackField } from "../ui/attack-field";
import { drawFullThrowablePreview } from "../handlers/throwable-handler";
import { spawnPlayerLabel } from "../ui/damage-text";
import { playSound } from "../utils/sound-utils";
import { StaminaMeter } from "../ui/stamina-meter";
import { EmojiPicker } from "../ui/emoji-picker";
import { calcMoveStep, clampToMapBounds } from "../utils/movement-utils";
import {
  getItemStat,
  Item,
  PLAYER_SPRINT_MULTIPLIER,
  PLAYER_UNARMED_ATTACK_INTERVAL,
  PLAYER_UNARMED_ATTACK_RANGE,
  TILE_SIZE,
} from "../models/common";

export function setupDesktopControls(scene: any, localOnly: boolean = false) {
  // Sprint is always active in the market scene and costs no stamina there
  const isMarketScene = scene.scene.key === "MarketScene";

  // Track pressed keys
  const keyState = { W: false, A: false, S: false, D: false, SHIFT: false };
  scene._desktopKeyState = keyState;

  // Attack state management
  let isAttacking = false;
  let currentAttackPointer = { worldX: 0, worldY: 0 };
  let lastAttackTime = 0;
  let aimStartTime = 0; // Track when aiming started
  let isAiming = false; // Track if we're currently in the aiming phase
  let hasAimed = false; // Track if we've completed aiming for this attack sequence

  // Flag to control whether update loop is active
  let isMovementUpdateActive = false;
  let isAttackUpdateActive = false;

  // Sprint and stamina management
  let isSprinting = false;
  let staminaMeter: StaminaMeter | null = null;
  let lastDustTileX = -1;
  let lastDustTileY = -1;

  // Throwable state management
  let activeThrowableItem: Item | null = null;
  let isThrowableMode = false;
  let isThrowableAiming = false;
  let throwableIndicator: Phaser.GameObjects.Graphics | null = null;

  // Set up throwable event listeners
  scene.events.on("throwable-activated", (item: Item) => {
    activeThrowableItem = item;
    isThrowableMode = true;
    isThrowableAiming = false;
  });

  // Movement update handler (called by scene update event)
  const handleMovementUpdate = (_time: number, delta: number) => {
    if (!isMovementUpdateActive) return;

    // Don't allow movement if any window is open
    if (scene.windowManager && scene.windowManager.isAnyWindowOpen()) {
      return;
    }

    if (scene.player && scene.player.isAlive()) {
      const { dx, dy } = getMovementVector();
      if (dx !== 0 || dy !== 0) {
        // Apply sprint multiplier if sprinting and moving
        const currentStamina = staminaMeter ? staminaMeter.getStamina() : 0;
        const sprintMultiplier =
          isMarketScene || (isSprinting && keyState.SHIFT && currentStamina > 0)
            ? PLAYER_SPRINT_MULTIPLIER
            : 1;
        const moveStep = calcMoveStep(delta, sprintMultiplier);
        // Run the body/hat animation twice as fast while sprinting
        scene.player.setSprinting?.(sprintMultiplier > 1);

        if (localOnly) {
          // Calculate intended new position with bounds checking
          const newX = scene.player.x + dx * moveStep;
          const newY = scene.player.y + dy * moveStep;
          const clampedPos = clampToMapBounds(newX, newY, scene);

          const checkOffsetY = dy > 0 ? 16 : 0;
          const checkX = clampedPos.x;
          const checkY = clampedPos.y + checkOffsetY;

          if (!scene.isBlocked(checkX, checkY)) {
            if (dx < 0) {
              scene.player.setFacing(true);
            } else if (dx > 0) {
              scene.player.setFacing(false);
            }
            scene.player.x = clampedPos.x;
            scene.player.y = clampedPos.y;
            scene.player.targetX = clampedPos.x;
            scene.player.targetY = clampedPos.y;
            scene.player.container.setPosition(clampedPos.x, clampedPos.y);
            scene.player.startMovingAnimation();
            // Spawn dust particle if sprinting and on a new tile
            if (isSprinting && scene.player.spawnDustParticle) {
              const tileX = Math.floor(clampedPos.x / TILE_SIZE);
              const tileY = Math.floor(clampedPos.y / TILE_SIZE);
              if (tileX !== lastDustTileX || tileY !== lastDustTileY) {
                scene.player.spawnDustParticle();
                lastDustTileX = tileX;
                lastDustTileY = tileY;
              }
            }
            if (scene.player.onMoveCallback) {
              scene.player.onMoveCallback();
            }
            if (scene.checkEffectTile(clampedPos.x, clampedPos.y)) {
              scene.sound.stopAll();
              playSound(scene, "teleport");
              stopMovementUpdate();

              // Check if player is in BaseScene at marketplace teleport (24, 9-10).
              // Expand range by 1 tile in each direction to account for checkEffectTile's
              // 12px offset radius, which can detect a tile when the player's center is
              // in an adjacent tile.
              const tileX = Math.floor(clampedPos.x / TILE_SIZE);
              const tileY = Math.floor(clampedPos.y / TILE_SIZE);
              const isMarketplaceTeleport = scene.scene.key === "BaseScene" && (tileX >= 23 && tileX <= 25) && (tileY >= 8 && tileY <= 11);

              if (isMarketplaceTeleport) {
                // Enter marketplace
                scene.enterMarketplace();
              } else {
                const squadCode = localStorage.getItem("squadCode");
                if (squadCode) {
                  if (window.socket) {
                    window.socket.emit("squad-teleport");
                  }
                } else {
                  scene.enterLobby();
                }
              }
              return;
            }
          }
        } else {
          const newX = scene.player.x + dx * moveStep;
          const newY = scene.player.y + dy * moveStep;
          const clampedPos = clampToMapBounds(newX, newY, scene);

          const checkOffsetY = dy > 0 ? 12 : 0;
          const checkX = clampedPos.x;
          const checkY = clampedPos.y + checkOffsetY;

          if (!scene.isBlocked(checkX, checkY)) {
            if (dx < 0) {
              scene.player.setFacing(true);
            } else if (dx > 0) {
              scene.player.setFacing(false);
            }
            scene.player.x = clampedPos.x;
            scene.player.y = clampedPos.y;
            scene.player.targetX = clampedPos.x;
            scene.player.targetY = clampedPos.y;
            scene.player.container.setPosition(clampedPos.x, clampedPos.y);

            if (scene.updateMovementDirection) {
              scene.updateMovementDirection(-dx, -dy);
            }

            scene.player.startMovingAnimation();
            // Spawn dust particle if sprinting and on a new tile
            if (isSprinting && scene.player.spawnDustParticle) {
              const tileX = Math.floor(clampedPos.x / TILE_SIZE);
              const tileY = Math.floor(clampedPos.y / TILE_SIZE);
              if (tileX !== lastDustTileX || tileY !== lastDustTileY) {
                scene.player.spawnDustParticle();
                lastDustTileX = tileX;
                lastDustTileY = tileY;
              }
            }
            if (scene.player.onMoveCallback) {
              scene.player.onMoveCallback();
            }
          }
        }
      }
    }
  };

  // Attack update handler (called by scene update event with throttling)
  const ATTACK_CHECK_INTERVAL = 50; // milliseconds
  let lastAttackCheckTime = -ATTACK_CHECK_INTERVAL; // Initialize so first check runs immediately
  const handleAttackUpdate = (time: number) => {
    if (!isAttackUpdateActive) return;

    // Throttle attack checks to 50ms intervals
    if (time - lastAttackCheckTime < ATTACK_CHECK_INTERVAL) {
      return;
    }
    lastAttackCheckTime = time;

    if (!scene.player || !scene.player.isAlive()) {
      return;
    }

    let attackIntervalMs = PLAYER_UNARMED_ATTACK_INTERVAL;
    let aimDelayMs = 0;
    if (
      window.player &&
      window.player.data &&
      window.player.data.equipment &&
      window.player.data.equipment.Weapon
    ) {
      const weapon: Item = window.player.data.equipment.Weapon;
      attackIntervalMs =
        getItemStat(weapon, "attackInterval") ?? PLAYER_UNARMED_ATTACK_INTERVAL;
      aimDelayMs = getItemStat(weapon, "aimDelay") ?? 0;
    }

    const now = Date.now();

    if (isAiming) {
      const timeSinceAimStart = now - aimStartTime;
      if (timeSinceAimStart >= aimDelayMs) {
        isAiming = false;
        hasAimed = true;
        lastAttackTime = now - attackIntervalMs;
      } else {
        return;
      }
    }

    const timeSinceLastAttack = now - lastAttackTime;

    if (isAttacking && timeSinceLastAttack >= attackIntervalMs) {
      const dx = currentAttackPointer.worldX - scene.player.x;
      const dy = currentAttackPointer.worldY - scene.player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      let direction = { x: 1, y: 0 };
      if (dist > 0.01) {
        direction = { x: dx / dist, y: dy / dist };
      }
      if (!localOnly) {
        scene.player.createAttackCooldownIndicator(scene);
        (window as any).socket.emit("player_attack", {
          attackerId: scene.player.socketId,
          x: scene.player.x,
          y: scene.player.y,
          direction,
        });
      }
      lastAttackTime = now;
    }
  };

  // Store attack state and cleanup function on scene for cleanup
  scene._desktopAttackState = {
    isAttacking: () => isAttacking,
    stopAttacking: () => {
      isAttacking = false;
      stopAttackUpdate();
    },
  };

  function getMovementVector() {
    let dx = 0,
      dy = 0;
    if (keyState.A) dx -= 1;
    if (keyState.D) dx += 1;
    if (keyState.W) dy -= 1;
    if (keyState.S) dy += 1;
    // Normalize to prevent diagonal movement from being faster
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 0) {
      dx /= dist;
      dy /= dist;
    }
    return { dx, dy };
  }

  function startMovementUpdate() {
    if (!isMovementUpdateActive) {
      isMovementUpdateActive = true;
      scene.events.on("update", handleMovementUpdate);
    }
  }

  function stopMovementUpdate() {
    if (isMovementUpdateActive) {
      isMovementUpdateActive = false;
      scene.events.off("update", handleMovementUpdate);
    }
  }

  function startAttackUpdate() {
    if (!isAttackUpdateActive) {
      isAttackUpdateActive = true;
      scene.events.on("update", handleAttackUpdate);
    }
  }

  function stopAttackUpdate() {
    if (isAttackUpdateActive) {
      isAttackUpdateActive = false;
      scene.events.off("update", handleAttackUpdate);
    }
  }

  // Throwable helper functions
  function updateThrowableIndicator(pointer: any) {
    if (!isThrowableMode || !isThrowableAiming || !scene.player) return;

    if (!throwableIndicator) {
      throwableIndicator = scene.add.graphics();
      if (throwableIndicator) {
        throwableIndicator.setDepth(9999);

        // Make sure UI camera ignores this graphics (game world element)
        if ((scene as any).uiCamera) {
          (scene as any).uiCamera.ignore(throwableIndicator);
        }
      }
    }
    if (!throwableIndicator) return;
    throwableIndicator.clear();

    // Use the sprite's world position instead of just the logical player position
    const playerX = scene.player.container.x;
    const playerY = scene.player.container.y;
    const targetX = pointer.worldX;
    const targetY = pointer.worldY;

    const dx = targetX - playerX;
    const dy = targetY - playerY;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Limit throw distance (same as touch controls: max 200 pixels)
    const maxDistance = 200;
    const actualDistance = Math.min(distance, maxDistance);

    if (actualDistance > 5) {
      const angle = Math.atan2(dy, dx);
      const endX = playerX + Math.cos(angle) * actualDistance;
      const endY = playerY + Math.sin(angle) * actualDistance;

      drawFullThrowablePreview(
        throwableIndicator,
        playerX,
        playerY,
        endX,
        endY,
        {
          color: 0xffff00,
          arcHeight: 50,
          steps: 24,
          showTargetCircle: true,
          showMarkers: true,
          targetRadius: 15,
          markerCount: 3,
        },
      );
    }
  }

  function clearThrowableIndicator() {
    if (throwableIndicator) {
      throwableIndicator.destroy();
      throwableIndicator = null;
    }
  }

  function resetThrowableMode() {
    isThrowableMode = false;
    isThrowableAiming = false;
    clearThrowableIndicator();

    // Stop blinking on action slots
    if (scene.windowManager?.actionSlots) {
      scene.windowManager.actionSlots.forEach((slot: any) =>
        slot.stopBlinking(),
      );
    }
  }

  if (scene.input && scene.input.keyboard) {
    scene.cursors = scene.input.keyboard.createCursorKeys();
    scene.keys = {
      W: scene.input.keyboard.addKey(87, false), // W - false = don't prevent default
      A: scene.input.keyboard.addKey(65, false), // A
      S: scene.input.keyboard.addKey(83, false), // S
      D: scene.input.keyboard.addKey(68, false), // D
      SHIFT: scene.input.keyboard.addKey(16, false), // SHIFT
    };
    // Add client-side cooldown for firing
    scene.lastAttackTime = 0;

    // Create stamina meter - pass uiLayer so it's always rendered by uiCamera
    // regardless of when this is called relative to the uiCamera ignore-loop
    const gameWidth = scene.scale.width;
    let staminaMeterX = gameWidth / 2;
    const staminaMeterY = scene.scale.height - 20;
    staminaMeter = new StaminaMeter(scene, staminaMeterX, staminaMeterY, scene.uiLayer);

    // Hide stamina meter in BaseScene (localOnly mode)
    if (localOnly) {
      staminaMeter.hide();
    }

    // Create emoji button in lower right corner
    const emojiX = scene.scale.width - 50;
    const emojiY = scene.scale.height - 35;
    let desktopEmojiPicker: EmojiPicker | null = null;
    const desktopEmojiButton = scene.add.image(emojiX, emojiY, "emoticons-icon");
    desktopEmojiButton.setDisplaySize(34, 34);
    desktopEmojiButton.setScrollFactor(0);
    desktopEmojiButton.setDepth(10099);
    desktopEmojiButton.setVisible(!localOnly);
    desktopEmojiButton.setInteractive({ useHandCursor: true });
    desktopEmojiButton.on("pointerdown", () => {
      if (desktopEmojiPicker && desktopEmojiPicker.isActive()) {
        desktopEmojiPicker.destroy();
        desktopEmojiPicker = null;
        return;
      }
      desktopEmojiPicker = new EmojiPicker(
        scene,
        emojiX,
        emojiY - 4,
        scene.uiLayer,
        (selection) => {
          desktopEmojiPicker = null;
          if (!window.socket || !scene.player) return;
          if (selection.kind === "emote") {
            window.socket.emit("player-emoji", { emoji: selection.key });
          } else {
            scene.enterStickerPlacementMode?.(selection.sprayId);
          }
        },
      );
    });
    if (scene.uiLayer) {
      scene.uiLayer.add(desktopEmojiButton);
    }

    if (scene.input.keyboard) {
      scene.input.keyboard.on("keydown", (event: KeyboardEvent) => {
        // Check if user is typing in an HTML input element
        const activeElement = document.activeElement;
        if (
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA")
        ) {
          return; // Allow normal typing in inputs
        }

        // Don't capture WASD keys if any window (including input popups) is open
        if (scene.windowManager && scene.windowManager.isAnyWindowOpen()) {
          return;
        }

        // Don't capture WASD keys if tutorial is visible
        if (scene.windowManager && scene.windowManager.isTutorialVisible()) {
          return;
        }

        // Handle escape key for throwable mode
        if (event.key === "Escape" && isThrowableMode) {
          resetThrowableMode();
          return;
        }

        switch (event.key.toUpperCase()) {
          case "W":
            keyState.W = true;
            break;
          case "A":
            keyState.A = true;
            break;
          case "S":
            keyState.S = true;
            break;
          case "D":
            keyState.D = true;
            break;
          case "SHIFT":
            keyState.SHIFT = true;
            break;
          default:
            return;
        }
        startMovementUpdate();
      });
      scene.input.keyboard.on("keyup", (event: KeyboardEvent) => {
        // Check if user is typing in an HTML input element
        const activeElement = document.activeElement;
        if (
          activeElement &&
          (activeElement.tagName === "INPUT" ||
            activeElement.tagName === "TEXTAREA")
        ) {
          return; // Allow normal typing in inputs
        }

        // Don't capture WASD keys if any window (including input popups) is open
        if (scene.windowManager && scene.windowManager.isAnyWindowOpen()) {
          return;
        }

        // Don't capture WASD keys if tutorial is visible
        if (scene.windowManager && scene.windowManager.isTutorialVisible()) {
          return;
        }

        switch (event.key.toUpperCase()) {
          case "W":
            keyState.W = false;
            break;
          case "A":
            keyState.A = false;
            break;
          case "S":
            keyState.S = false;
            break;
          case "D":
            keyState.D = false;
            break;
          case "SHIFT":
            keyState.SHIFT = false;
            break;
          default:
            return;
        }
        // If no movement keys are held, stop loop
        if (!keyState.W && !keyState.A && !keyState.S && !keyState.D) {
          stopMovementUpdate();
        }
      });
    }

    // Draw attack field (cone/line) around player
    let attackFieldGraphics: Phaser.GameObjects.Graphics | null = null;
    scene.events.on("postupdate", () => {
      if (scene.player && scene.player.isAlive()) {
        // Remove previous field
        if (attackFieldGraphics) {
          attackFieldGraphics.destroy();
          attackFieldGraphics = null;
        }
        // Get equipped weapon stats or use unarmed defaults
        let range = 60;
        let accuracy = 100;
        let spreadAngle = undefined;
        const weapon: Item = scene.player.data?.equipment?.Weapon;
        if (weapon && weapon.definition.stats) {
          if (weapon.definition.stats.range)
            range = weapon.definition.stats.range;
          if (weapon.definition.stats.accuracy !== undefined)
            accuracy = weapon.definition.stats.accuracy;
        } else {
          // Use unarmed attack range and accuracy
          range = PLAYER_UNARMED_ATTACK_RANGE;
          accuracy = 100;
          spreadAngle = Math.PI / 2; // 90deg cone for unarmed
        }

        // Use world position for field origin
        const px = scene.player.container.x;
        const py = scene.player.container.y + WEAPON_Y_OFFSET;
        // Get aim direction from mouse pointer
        const pointer = scene.input.activePointer;
        // Always convert pointer position to world coordinates using camera
        const camera =
          scene.cameras && scene.cameras.main ? scene.cameras.main : null;
        let pointerWorldX = pointer.worldX;
        let pointerWorldY = pointer.worldY;
        if (
          camera &&
          typeof pointer.x === "number" &&
          typeof pointer.y === "number"
        ) {
          pointerWorldX = camera.getWorldPoint(pointer.x, pointer.y).x;
          pointerWorldY = camera.getWorldPoint(pointer.x, pointer.y).y;
        }
        const dx = pointerWorldX - px;
        const dy = pointerWorldY - py;
        let direction = { x: 1, y: 0 };
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > 0.01) {
          direction = { x: dx / dist, y: dy / dist };
        }

        // Rotate player's weapon to follow mouse
        if (scene.player.weaponSprite && scene.player.weaponSprite.visible) {
          scene.player.rotateWeaponToPoint(pointerWorldX, pointerWorldY);
        }

        // Draw attack field following the mouse cursor
        attackFieldGraphics = drawAttackField(
          scene,
          px,
          py,
          direction,
          range,
          accuracy,
          0x00ffff,
          0.1,
          spreadAngle,
        );
        // Set depth below player sprite (player is at depth 2)
        if (attackFieldGraphics) {
          attackFieldGraphics.setDepth(1);
        }
      } else {
        if (attackFieldGraphics) {
          attackFieldGraphics.destroy();
          attackFieldGraphics = null;
        }
      }
    });

    // Mouse event handling - supports both attack and throwable modes
    scene.input.on("pointerdown", (pointer: any) => {
      if (!scene.player || !scene.player.isAlive()) return;

      // Don't allow attacks if any window is open
      if (scene.windowManager && scene.windowManager.isAnyWindowOpen()) {
        return;
      }

      // Don't allow attacks if clicking on interactive UI elements
      // Check if the pointer is over any interactive game object
      const hitObjects = scene.input.hitTestPointer(pointer);
      if (hitObjects && hitObjects.length > 0) {
        // If any hit object is interactive and part of the UI, ignore the attack
        for (const obj of hitObjects) {
          if (obj.input && obj.input.enabled) {
            // This is an interactive object (likely a UI element), don't attack
            return;
          }
        }
      }

      // Calculate distance and direction for both modes
      const dx = pointer.worldX - scene.player.x;
      const dy = pointer.worldY - scene.player.y;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (isThrowableMode) {
        // Start throwable aiming mode
        isThrowableAiming = true;
        updateThrowableIndicator(pointer);
        return;
      }

      // Normal attack mode - can attack anywhere

      // Start continuous attacking
      isAttacking = true;
      currentAttackPointer = { worldX: pointer.worldX, worldY: pointer.worldY };

      // Get aim delay from equipped weapon
      let aimDelayMs = 0;
      let hasAmmo = true;
      if (
        window.player &&
        window.player.data &&
        window.player.data.equipment &&
        window.player.data.equipment.Weapon
      ) {
        const weapon: Item = window.player.data.equipment.Weapon;
        if (weapon.definition.stats && weapon.definition.stats.aimDelay) {
          aimDelayMs = weapon.definition.stats.aimDelay; // Convert seconds to milliseconds
        }
        // Check if weapon has ammo
        hasAmmo = weapon.currentAmmo === undefined || weapon.currentAmmo > 0;
      }

      // If weapon has aim delay, has ammo, and we haven't aimed yet in this sequence, start aiming
      if (aimDelayMs > 0 && hasAmmo && !hasAimed) {
        isAiming = true;
        aimStartTime = Date.now();
        scene.player.createAimCooldownIndicator(scene);
        // Show "HOLD TO AIM" status label
        spawnPlayerLabel(
          "HOLD TO SHOOT",
          scene.player.x,
          scene.player.y - 48,
          scene,
          "#ffdd00",
        );
      }

      // Start attack check interval if not already running
      if (!isAttackUpdateActive) {
        startAttackUpdate();
      }
    });

    scene.input.on("pointermove", (pointer: any) => {
      // Don't allow movement updates if any window is open
      if (scene.windowManager && scene.windowManager.isAnyWindowOpen()) {
        return;
      }

      // Don't update if player is dead
      if (!scene.player || !scene.player.isAlive()) {
        return;
      }

      if (isThrowableAiming) {
        // Update throwable trajectory preview
        updateThrowableIndicator(pointer);
      } else if (isAttacking) {
        // Update attack direction while holding down
        currentAttackPointer = {
          worldX: pointer.worldX,
          worldY: pointer.worldY,
        };
      }
    });

    scene.input.on("pointerup", (pointer: any) => {
      if (isThrowableAiming && scene.player && scene.player.isAlive()) {
        // Throw the grenade
        const playerX = scene.player.container.x;
        const playerY = scene.player.container.y;
        const dx = pointer.worldX - playerX;
        const dy = pointer.worldY - playerY;
        const distance = Math.sqrt(dx * dx + dy * dy);
        const maxDistance = 200;
        const actualDistance = Math.min(distance, maxDistance);

        if (actualDistance > 5) {
          const direction = { x: dx / distance, y: dy / distance };

          // Emit throwable release event
          scene.events.emit("throwable-release", {
            itemDefId: activeThrowableItem?.definition.id,
            startX: playerX,
            startY: playerY,
            direction,
            distance: actualDistance,
          });
        }
        // Reset throwable mode
        resetThrowableMode();
        return;
      }

      // Stop continuous attacking and reset aim state
      isAttacking = false;

      // If aiming was in progress, immediately destroy aim cooldown bars
      if (isAiming && scene.player && scene.player.aimCooldownBars) {
        scene.player.aimCooldownBars.forEach((bar: any) => {
          if (bar && typeof bar.destroy === "function") {
            bar.destroy();
          }
        });
        scene.player.aimCooldownBars = [];
      }

      isAiming = false;
      hasAimed = false;
      // Note: We don't stop attack update here - it continues to tick down cooldown
    });

    // --- Stamina update loop ---
    scene.events.on("update", (time: number, delta: number) => {
      if (!scene.player || !scene.player.isAlive() || !staminaMeter) return;

      // Check if sprinting and moving
      const currentStamina = staminaMeter.getStamina();
      const isMoving = keyState.W || keyState.A || keyState.S || keyState.D;
      const isSprintingAndMoving = isMarketScene
        ? isMoving
        : keyState.SHIFT && isMoving && currentStamina > 0;

      if (isSprintingAndMoving) {
        // Deplete stamina while sprinting
        isSprinting = true;
        // Skip stamina depletion if admin mode is active, or in the market scene where sprint is free
        if (!scene.player.adminMode && !isMarketScene) {
          staminaMeter.deplete(StaminaMeter.STAMINA_DEPLETION_RATE * delta);
        }

        // If stamina reaches 0, stop sprinting
        if (!isMarketScene && staminaMeter.getStamina() <= 0) {
          isSprinting = false;
        }
      } else {
        // Not sprinting - refill stamina at constant rate
        isSprinting = false;
        const staminaToAdd = StaminaMeter.STAMINA_REPLENISH_RATE * delta;
        staminaMeter.setStamina(currentStamina + staminaToAdd);
      }

      // Show meter when stamina is below max, hide when full
      if (staminaMeter.getStamina() < StaminaMeter.STAMINA_MAX) {
        staminaMeter.show();
      } else {
        staminaMeter.hide();
      }
    });

    // Cleanup when scene is destroyed or switched
    scene.events.once("shutdown", () => {
      stopMovementUpdate();
      stopAttackUpdate();
      scene.events.off("update"); // Remove stamina update listener
      if (staminaMeter) {
        staminaMeter.destroy();
        staminaMeter = null;
      }
      if (desktopEmojiPicker) {
        desktopEmojiPicker.destroy();
        desktopEmojiPicker = null;
      }
      if (desktopEmojiButton) {
        desktopEmojiButton.destroy();
      }
    });
  }
}

export function resetDesktopKeyStates(scene: any) {
  if (scene._desktopKeyState) {
    scene._desktopKeyState.W = false;
    scene._desktopKeyState.A = false;
    scene._desktopKeyState.S = false;
    scene._desktopKeyState.D = false;
  }
}

export function resetDesktopControls(scene: any) {
  resetDesktopKeyStates(scene);

  // Stop any continuous attacking and movement
  if (scene._desktopAttackState) {
    scene._desktopAttackState.stopAttacking();
  }

  if (scene.input && scene.input.keyboard) {
    // Release Phaser keys
    if (scene.keys) {
      Object.values(scene.keys).forEach((key) => {
        if ((key as any).isDown) (key as any).isDown = false;
      });
    }
  }

  scene.events.off("throwable-activated");
}
